/* eslint no-lone-blocks: 0 */
import * as React from 'react';
import * as R from 'ramda';
import {
 Control, FieldValues, useController, useFieldArray, FieldPath, ArrayPath, useWatch
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import { AsyncTable } from '../table';
import Icon, { IconTypes } from '../icon';
import { Button, ButtonType } from '../button';
import Modal from '../modal';
import Dropdown from '../dropdown';
import SearchBar from '../search-bar';

import * as organisationSelector from '../../../modules/organisation/selectors/organisation';

import Api from '../../services/api';
import createTempId from '../../utils/create-temp-id';
import { predicatesToPayload } from '@common/utils/predicates';

import type { Organisation } from '../../types/objects';
import type { StoreState } from '@common/types/store';
import type { ListApiResponse } from '@common/types/util-types';
import { ESearchPredicateTypes, EPredicateFields } from '@common/definitions';

import {
  PredicateFilter,
  EPredicateFilters,
  predicateTypes,
  PredicatesUserItem,
  PredicatesFilterOwnProps,
  PredicatesFilterValue
} from './predicates-filter';

import { PredicateSelectorAsyncMultiSelect } from './filter-async-multi-select';
import Label from '../form/label';
import { useLocation, useParams } from 'react-router';

type Option = Record<string, unknown>;

// Filters that allow multiple instances
const MULTIPLE_INSTANCE_FILTERS = [
  EPredicateFields.DAYS_IN_SERVICE,
  EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE,
];

function shouldDisableFilter(filterValue: EPredicateFields, existingFields: EPredicateFields[]): boolean {
  // Allow multiple instances for specific filters
  if (MULTIPLE_INSTANCE_FILTERS.includes(filterValue)) {
    return false;
  }
  // Disable if already exists for other filters
  return existingFields.includes(filterValue);
}

type PredicatesListHookFormProps<T extends FieldValues> = {
  name: string;
  control: Control<T>;
  predicateType: string;
  data: Object;
  enabledFilters: string[];
  onAddFilterClick?: () => boolean;
  onFilterSelect?: (type: `${EPredicateFields}`) => boolean;
  getState: () => StoreState;
  organisation: Organisation;
  addContextToPredicatesOnChange: boolean;
  searchResult?: any;
};

// Hook Form version of PredicatesList
function PredicatesListHookForm<T extends FieldValues>({
  name,
  control,
  predicateType,
  data,
  enabledFilters,
  getState,
  organisation,
  searchResult,
  addContextToPredicatesOnChange,
  onAddFilterClick = () => true,
  onFilterSelect = () => true,
}: PredicatesListHookFormProps<T>) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as ArrayPath<T>,
  });

  const filters = R.pipe(
    // @ts-expect-error
    R.map((key) => EPredicateFilters[key]),
    R.groupBy(R.prop('category')),
  )(enabledFilters);

  const predicateTypeLabel = t(
    predicateType === ESearchPredicateTypes.MATCH_ALL
      ? 'common:utils_predicates_and'
      : 'common:utils_predicates_or'
  );

  const elems: any[] = [];

  fields.forEach((field, index) => {
    const predicate = field as any;

    if (index > 0) {
      const attr = predicate.attribute;
      elems.push(
        <div
          className={`PredicatesFilter__Filters__PredicateType before-${attr}`}
          key={`${predicate.id}_type`}
        >
          {predicateTypeLabel}
        </div>
      );
    }

    const filter = EPredicateFilters[predicate.attribute];
    if (!filter || (filter.getData && !searchResult)) {
      return null;
    }

    const optionsData = filter.getData ?
      filter.getData(predicate.value, getState(), searchResult) :
      // @ts-expect-error
      filter.optionsKey && data[filter.optionsKey];

    // this fixed PD-8191 for all predicate filters types
    const options = optionsData?.filter((o: any) => !!o) || [];

    elems.push(
      <PredicateInputHookForm
        key={predicate.id}
        className={`order_${index}`}
        name={`${name}.${index}` as FieldPath<T>}
        control={control}
        filter={filter}
        options={options}
        getState={getState}
        organisation={organisation}
        onRemove={() => remove(index)}
        addContextToPredicatesOnChange={addContextToPredicatesOnChange}
      />
    );
  });

  const existingFields = fields.map((field: any) => field.attribute);

  return (
    <>
      {elems}

      <Dropdown
        small
        onClick={onAddFilterClick}
        toggle={(
          <Button type="transparent" iconRight="add" size="large">
            <Trans i18nKey="common:predicates_filter_add_filter" />
          </Button>
        )}
      >
        {R.values(R.mapObjIndexed((category, key) => {
          // @ts-expect-error
          const handleAddPredicate = (filter) => append({
            id: createTempId(),
            open: true,
            attribute: filter.value,
            comparison: filter.defaultOperator,
            value: filter.defaultValue,
          } as any);

          return (
            <React.Fragment key={key}>
              <Dropdown.Header>
                <Trans
                  i18nKey="common:predicates_filter_category"
                  values={{ context: key }}
                />
              </Dropdown.Header>
              {category.map((filter) => (
                <Dropdown.Item
                  className={`${filter.value}_option`}
                  key={filter.value}
                  onClick={() => onFilterSelect(filter.value) && handleAddPredicate(filter)}
                  disabled={shouldDisableFilter(filter.value, existingFields)}
                >
                  <Icon type={filter.icon} />
                  <Trans
                    i18nKey="common:predicates_filter_predicate"
                    values={{ context: filter.context }}
                  />
                </Dropdown.Item>
              ))}
            </React.Fragment>
          );
        }, filters))}
      </Dropdown>
    </>
  );
}

type PredicateInputHookFormProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  filter: PredicateFilter;
  options: Option[];
  organisation: Organisation;
  onRemove: () => void;
  addContextToPredicatesOnChange: boolean;
  className?: string;
  scrolling?: boolean;
  buttonType?: ButtonType;
  getState?: () => StoreState;
};

// Hook Form version of PredicateInput
function PredicateInputHookForm<T extends FieldValues>({
  name,
  control,
  filter,
  options,
  organisation,
  onRemove,
  addContextToPredicatesOnChange,
  className,
  scrolling = true,
  buttonType = 'default',
}: PredicateInputHookFormProps<T>) {
  const { field } = useController({
    name,
    control,
  });

  const predicate = field.value || {};
  const { onChange } = field;

  const [isOpen, setOpen] = React.useState(!!(predicate as any).open);
  const [selectedOptions, setSelectedOptions] = React.useState(options);

  // @ts-expect-error
  const preview = filter.previewer?.(predicate, filter, filter.getOptions ? selectedOptions : options);

  React.useEffect(() => {
    if ((predicate as any).open) {
      onChange(R.omit(['open'], predicate));
    }
  }, [(predicate as any).open, onChange]);

  const FilterComponent = filter.selector;

  return (
    <Dropdown
      className={`${filter.value} ${className || ''}`}
      small
      defaultOpen={!!(predicate as any).open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);

        // Remove predicates which are "empty"
        if (onRemove && (!(predicate as any).value ||
        (Array.isArray((predicate as any).value) && (predicate as any).value.length === 0))) {
          return onRemove();
        }
      }}
      scrolling={scrolling}
      toggle={(
        <Button type={buttonType} size="large" active={isOpen} icon={filter.icon as IconTypes}>
          <Trans i18nKey="common:predicates_filter_predicate" values={{ context: filter.context }} />
          {preview && (
            <span style={{ fontWeight: 400 }}>
              &nbsp;
              {preview}
            </span>
          )}
          {isOpen && onRemove && (
            <Icon
              type="close"
              className="PredicatesFilter__Filters__Remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
                setOpen(false);
              }}
            />
          )}
        </Button>
      )}
      search={FilterComponent === PredicateSelectorAsyncMultiSelect}
    >
      <FilterComponent
        valueKey="id"
        labelKey="name"
        predicate={predicate}
        organisation={organisation}
        updateSelectedOptions={setSelectedOptions}
        // @ts-expect-error
        options={filter.getOptions ? selectedOptions : options}
        {...filter}
        onChange={(newValue: any) => {
          try {
            const newPredicates: any = R.merge(predicate, newValue);
            if (addContextToPredicatesOnChange) {
              // @ts-expect-error
              const opts = filter.getOptions ? selectedOptions : options;
              let context: any;
              if (Array.isArray(newPredicates.value)) {
                context = newPredicates.value.map((id: string) => {
                  return opts.find((option: any) => option.id === id);
                }).filter((option: any) => !!option);
              } else {
                context = newPredicates.value;
              }
              newPredicates.context = context;
            }
            return onChange(newPredicates);
          } catch (error) {
            console.log('debug error', error);
            throw error;
          }
        }}
      />
    </Dropdown>
  );
}

const getStateGetter = (state: StoreState, props: any) => {
  // this getInjectedState allows us to create a fake redux state so we
  // can provide custom data to the component instead of relying on redux
  // state
  if (props.getInjectedState) {
    return () => props.getInjectedState(state);
  }
  // previous solution to problem explained above, but it forces us to extract
  // the redux state in the parent components and have it consumed by
  // props.getState
  if (props.getState) {
    return props.getState;
  }

  // this just returns the state, so the AudienceInput will just read the
  // actual redux data
  return () => state;
};

const mapStateToProps = (state: StoreState, props: any) => {
  const getState = getStateGetter(state, props);
  const possiblyInjectedReduxState = getState();
  const locales = possiblyInjectedReduxState.language.translations;
  return {
    organisation: organisationSelector.selected(possiblyInjectedReduxState),
    networks: organisationSelector.networks(possiblyInjectedReduxState),
    functions: organisationSelector.functions(possiblyInjectedReduxState),
    languageLocales: locales.map((value: any) => {
      return { ...value, id: value.locale };
    }),
    getState
  };
};

const mapDispatchToProps = {};

const predicatesFilterConnector = connect(mapStateToProps, mapDispatchToProps);

type PredicatesFilterConnectedProps = ConnectedProps<typeof predicatesFilterConnector>;

type PredicatesFilterHookFormProps<T extends FieldValues> = PredicatesFilterConnectedProps & PredicatesFilterOwnProps & {
  name: FieldPath<T>;
  control: Control<T>;
  predicateTypeName: FieldPath<T>;
  predicatesName: string;
};

type PredicateFilterApiResponse = ListApiResponse<
  Record<string, unknown>[],
  undefined,
  { pagination: { total_count: number } }
>;

const defaultFilters = [
  EPredicateFields.USER,
  EPredicateFields.LANGUAGE,
  EPredicateFields.DAYS_IN_SERVICE,
  EPredicateFields.NETWORK,
  EPredicateFields.FUNCTION,
  EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE,
];

type AudienceModalProps = {
  children: React.ReactNode;
  show: boolean;
};

function PredicatesFilterHookFormComponent<T extends FieldValues>({
  name,
  control,
  predicateTypeName,
  predicatesName,
  organisation,
  onAddFilterClick,
  onFilterSelect,
  getSearchResults,
  description,
  enabledFilters = defaultFilters,
  getState,
  tooltips = {},
  addContextToPredicatesOnChange = false,
  ...data
}: PredicatesFilterHookFormProps<T>) {
  const { t } = useTranslation();
  const [searchResult, setSearchResult] = React.useState<PredicateFilterApiResponse | null>(null);
  const [showAudienceModal, setShowAudienceModal] = React.useState(false);
  const { networkId, id } = useParams<{ networkId: string, id: string }>();
  const { pathname } = useLocation();
  const baseUrl = pathname.includes('/networks/') || networkId ? `/networks/${networkId || id}` : '/admin';
  const audience = useWatch({
    control,
    name,
  }) || { predicate_type: ESearchPredicateTypes.MATCH_ALL, predicates: [] };

  const predicateType = useWatch({
    control,
    name: predicateTypeName,
  }) || ESearchPredicateTypes.MATCH_ALL;

  const columns = [
    { size: 35 },
    { label: t('common:predicates_filter_column_name_label'), className: 'Table__Cell__Title' },
    { label: t('common:predicates_filter_column_communities_label') },
    { label: t('common:predicates_filter_column_functions_label') },
  ];

  const onFetch = React.useCallback((offset: number | undefined, filter: PredicatesFilterValue, limit = 10) => {
    const query = Api.utils.toQuery({
      offset,
      limit,
      include: ['networks', 'functions'],
      q: filter.search,
    });

    return Api.post(`/v1/organisations/${organisation.id}/users/search?${query}`, {
      predicate_type: filter.predicate_type,
      predicates: predicatesToPayload(filter.predicates),
    });
  }, [organisation.id]);


  const AudienceModal = React.memo(({ children, show }: AudienceModalProps) => {
    const [audienceSearch, setAudienceSearch] = React.useState<string | null>(null);
    const [scrollContainer, setScrollContainer] = React.useState<HTMLDivElement | void>();

    const handleOnShow = () => {
      if (document) {
        // @ts-expect-error
        setScrollContainer(document.querySelector('.AudienceModal .modal-body'));
      }
    };

    return (
      <Modal
        size="large"
        title={t('common:predicates_filter_who_will_receive')}
        show={show}
        onShow={handleOnShow}
        // @ts-expect-error
        onHide={() => setShowAudienceModal(false)}
        className="AudienceModal"
        header={(
          // @ts-expect-error
          <SearchBar defaultValue={audienceSearch} onSearch={setAudienceSearch} />
        )}
        content={(
          <AsyncTable
            // @ts-expect-error
            scrollContainer={scrollContainer}
            columns={columns}
            data={{
              onFetch,
              limit: 25,
              cache: searchResult && {
                items: searchResult.data,
                pagination: searchResult.meta.pagination,
              },
              filter: {
                ...audience,
                search: audienceSearch,
              },
            }}
            renderRow={PredicatesUserItem}
            rowProps={{
              maxShownItems: 2,
              baseUrl,
            }}
          />
        )}
      >
        {children}
      </Modal>
    );
  });

  return (
    <div className="PredicatesFilter">
      {/* @ts-expect-error */}
      <AudienceModal show={showAudienceModal} />
      <div className="PredicatesFilter__Column PredicatesFilter__Column--size-large">
        <Label text={t('common:predicates_filter_who_will_receive')} description={tooltips.audience} />

        <div className="PredicatesFilter__Filters">
          <Dropdown
            small
            toggle={(
              <Button size="large">
                <Trans i18nKey="common:predicates_filter_type" values={{ context: predicateType }} />
              </Button>
            )}
          >
            {predicateTypes.map((type) => (
              <Dropdown.Item
                key={type}
                onClick={() => {
                  // Update the predicate type in the form
                  (control as any).setValue(predicateTypeName, type);
                }}
                selected={predicateType === type}
              >
                <Trans i18nKey="common:predicates_filter_type" values={{ context: type }} />
              </Dropdown.Item>
            ))}
          </Dropdown>

          <PredicatesListHookForm
            name={predicatesName}
            control={control}
            predicateType={predicateType}
            data={data}
            enabledFilters={enabledFilters}
            organisation={organisation}
            getState={getState}
            searchResult={searchResult}
            onAddFilterClick={onAddFilterClick}
            onFilterSelect={onFilterSelect}
            addContextToPredicatesOnChange={addContextToPredicatesOnChange}
          />
        </div>

        {searchResult && (
          <h3>
            <Trans
              i18nKey="common:predicates_filter_search_result"
              values={{ count: searchResult.meta.pagination.total_count }}
              components={[<b />]}
            />
            &nbsp;·&nbsp;
            <a onClick={() => setShowAudienceModal(true)}>
              <Trans i18nKey="common:predicates_filter_view_all" />
            </a>
          </h3>
        )}

        {description && <div className="PredicatesFilter__Description">{description}</div>}
        {/* @ts-expect-error */}
        <AsyncTable
          columns={columns}
          data={{
            onFetch,
            onPostFetch: (result: PredicateFilterApiResponse) => {
              setSearchResult(result);
              getSearchResults?.(result);
            },
            filter: audience,
          }}
          renderRow={PredicatesUserItem}
          rowProps={{
            baseUrl,
          }}
          ShowMoreComponent={({ count }: { count: number }) => (
            <div className="PredicatesFilter__ShowMore" onClick={() => setShowAudienceModal(true)}>
              <Trans i18nKey="common:predicates_filter_view_all_employees" values={{ count }} />
            </div>
          )}
          placeholder={(
            <div className="Placeholder">
              <div className="Placeholder__Inner">
                <Trans i18nKey="common:predicates_filter_search_result_placeholder" />
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

export const PredicatesFilterHookForm = predicatesFilterConnector(PredicatesFilterHookFormComponent);
